import { useEffect, useMemo, useRef, useState } from 'react';
import { Popover } from '@/components/Popover/Popover';
import { Avatar } from '@/components/Avatar/Avatar';
import { useMembers, type Member } from '@/features/members';
import { formatPersonName } from '@/lib/format';
import { t } from '@/lib/i18n';
import styles from './MemberDropdownPicker.module.css';

export interface MemberDropdownPickerProps {
  value: string | null;
  onChange: (id: string) => void;
}

/**
 * Step 2 dropdown picker for assigning an AI Member to a Job
 * (ui-design.md §5.2).
 *
 * Closed state: avatar + name + position + chevron.
 * Open state: searchable list of AI Members.
 *
 * Suggested-Members grouping is deferred until templates carry a `forPositions`
 * field.
 */
export function MemberDropdownPicker({ value, onChange }: MemberDropdownPickerProps) {
  const { data, isLoading } = useMembers();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const searchRef = useRef<HTMLInputElement | null>(null);

  // Focus the search input on open so users can type-to-filter immediately.
  // (Manual focus instead of autoFocus to keep keyboard users in control.)
  useEffect(() => {
    if (open) {
      const id = requestAnimationFrame(() => searchRef.current?.focus());
      return () => cancelAnimationFrame(id);
    }
    return undefined;
  }, [open]);

  const aiMembers: Member[] = (data?.items ?? []).filter(
    (m) => m.kind === 'ai' && m.archivedAt === null,
  );
  const selected = aiMembers.find((m) => m.id === value) ?? null;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return aiMembers;
    return aiMembers.filter((m) => {
      const name = formatPersonName(m).toLowerCase();
      const positions = m.positions.map((p) => p.name.toLowerCase()).join(' ');
      return name.includes(q) || positions.includes(q);
    });
  }, [aiMembers, query]);

  function handlePick(id: string) {
    onChange(id);
    setOpen(false);
    setQuery('');
  }

  if (isLoading) {
    return <div className={styles.loading}>{t('Loading members…')}</div>;
  }
  if (aiMembers.length === 0) {
    return <div className={styles.empty}>{t('No AI Members available — add one first.')}</div>;
  }

  return (
    <Popover
      open={open}
      onOpenChange={setOpen}
      align="start"
      sideOffset={4}
      className={styles.panel}
      trigger={
        <button
          type="button"
          className={styles.trigger}
          aria-haspopup="listbox"
          aria-expanded={open}
        >
          {selected ? <SelectedRow member={selected} /> : <PlaceholderRow />}
          <span className={styles.chevron} aria-hidden>
            ▾
          </span>
        </button>
      }
    >
      <div className={styles.panelInner}>
        <input
          ref={searchRef}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('Search members…')}
          aria-label={t('Search Members')}
          className={styles.search}
        />
        {filtered.length === 0 ? (
          <div className={styles.empty}>{t('No members match.')}</div>
        ) : (
          <ul className={styles.list} role="listbox" aria-label={t('AI Members')}>
            {filtered.map((m) => {
              const name = formatPersonName(m);
              const position = m.positions[0]?.name ?? null;
              const isSelected = m.id === value;
              return (
                <li key={m.id}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={isSelected}
                    className={`${styles.option} ${isSelected ? styles.optionSelected : ''}`}
                    onClick={() => handlePick(m.id)}
                  >
                    <Avatar name={name} kind={m.kind} />
                    <div className={styles.optionText}>
                      <span className={styles.optionName}>{name}</span>
                      {position ? <span className={styles.optionPosition}>{position}</span> : null}
                    </div>
                    {isSelected ? (
                      <span className={styles.optionCheck} aria-hidden>
                        ✓
                      </span>
                    ) : null}
                  </button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </Popover>
  );
}

function SelectedRow({ member }: { member: Member }) {
  const name = formatPersonName(member);
  const position = member.positions[0]?.name ?? null;
  return (
    <span className={styles.triggerRow}>
      <Avatar name={name} kind={member.kind} />
      <span className={styles.triggerText}>
        <span className={styles.triggerName}>{name}</span>
        {position ? <span className={styles.triggerPosition}>{position}</span> : null}
      </span>
    </span>
  );
}

function PlaceholderRow() {
  return (
    <span className={styles.triggerRow}>
      <span className={styles.placeholderAvatar} aria-hidden>
        ?
      </span>
      <span className={styles.triggerText}>
        <span className={styles.placeholder}>{t('Pick an AI Member')}</span>
      </span>
    </span>
  );
}
